import React from 'react';
import { Card, Space, Tag, Segmented } from 'antd';
import {
  UnorderedListOutlined,
  RightOutlined,
  BookOutlined,
  CheckCircleFilled,
  TrophyOutlined,
  EyeOutlined,
} from '@ant-design/icons';

export function TableOfContents({
  parts = [],
  activePartNo = 1,
  onSelectPart,
  viewMode = 'section',
  onChangeViewMode,
  hasSummary = false,
  onSelectSummary,
}) {
  if (!parts || parts.length === 0) {
    return null;
  }

  const handleItemClick = (p) => {
    if (viewMode === 'section') {
      if (onSelectPart) {
        onSelectPart(p.no);
      }
      // Cuộn mượt về khung bài giảng
      const contentEl = document.getElementById('lesson-content-area');
      if (contentEl) {
        const offset = 70;
        const topPos = contentEl.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, topPos), behavior: 'smooth' });
      }
    } else {
      // Chế độ cuộn toàn bài
      const partNo = p.no;
      let target =
        document.getElementById(`part-${partNo}`) ||
        document.getElementById(`phan-${partNo}`) ||
        (p.anchor && document.getElementById(p.anchor));

      if (!target) {
        const headings = document.querySelectorAll('.markdown-body h2, .markdown-body h3, h2, h3');
        for (const h of headings) {
          const text = (h.textContent || '').trim().toLowerCase();
          if (
            text.includes(`phần ${partNo}.`) ||
            text.includes(`phần ${partNo}:`) ||
            text.includes(`phần ${partNo} `) ||
            text.startsWith(`phần ${partNo}`) ||
            (p.title && text.includes(p.title.toLowerCase().substring(0, 20)))
          ) {
            target = h;
            break;
          }
        }
      }

      if (target) {
        const headerOffset = 70;
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth',
        });

        target.style.transition = 'all 0.4s ease';
        target.style.background = '#e0f2fe';
        target.style.padding = '6px 12px';
        target.style.borderRadius = '6px';
        target.style.boxShadow = '0 0 0 3px #38bdf8';

        setTimeout(() => {
          target.style.background = 'transparent';
          target.style.padding = '0';
          target.style.boxShadow = 'none';
        }, 2000);
      }
    }
  };

  const handleSummaryClick = () => {
    if (viewMode === 'section') {
      if (onSelectSummary) {
        onSelectSummary();
      }
      const contentEl = document.getElementById('lesson-content-area');
      if (contentEl) {
        const offset = 70;
        const topPos = contentEl.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, topPos), behavior: 'smooth' });
      }
    } else {
      const headings = document.querySelectorAll('.markdown-body h2');
      for (const h of headings) {
        const text = (h.textContent || '').trim().toLowerCase();
        if (text.includes('tóm tắt') || text.includes('tổng kết')) {
          const headerOffset = 70;
          const pos = h.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({ top: Math.max(0, pos), behavior: 'smooth' });
          break;
        }
      }
    }
  };

  return (
    <Card
      className="lesson-toc"
      style={{
        borderRadius: 14,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        marginBottom: 24,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
      }}
      styles={{ body: { padding: '20px 22px' } }}
    >
      {/* Header card with Mode Switch */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 14,
          paddingBottom: 12,
          borderBottom: '1px solid #dcfce7',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#005537',
            }}
          >
            <UnorderedListOutlined style={{ fontSize: 18 }} />
          </div>
          <div>
            <h4 style={{ fontSize: 15, fontWeight: 800, color: '#005537', margin: 0 }}>
              Mục Lục Bài Học ({parts.length} Phần Trọng Tâm)
            </h4>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {viewMode === 'section'
                ? '💡 Bấm vào phần bất kỳ để hiển thị nội dung phần đó (tập trung, dễ tiếp thu)'
                : 'Bấm vào phần bất kỳ để cuộn nhanh đến vị trí đó'}
            </span>
          </div>
        </div>

        {onChangeViewMode && (
          <Segmented
            value={viewMode}
            onChange={onChangeViewMode}
            options={[
              {
                value: 'section',
                label: (
                  <span style={{ fontWeight: 600, fontSize: 12 }}>
                    <BookOutlined /> Xem Từng Phần
                  </span>
                ),
              },
              {
                value: 'all',
                label: (
                  <span style={{ fontWeight: 600, fontSize: 12 }}>
                    <EyeOutlined /> Xem Toàn Bộ
                  </span>
                ),
              },
            ]}
          />
        )}
      </div>

      {/* List of Parts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {parts.map((p) => {
          const isActive = viewMode === 'section' && activePartNo === p.no;

          return (
            <div
              key={p.no}
              className="lesson-toc-row"
              onClick={() => handleItemClick(p)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '11px 16px',
                borderRadius: 10,
                cursor: 'pointer',
                background: isActive ? '#f0fdf4' : '#ffffff',
                border: isActive ? '2px solid #005537' : '1px solid #e2e8f0',
                boxShadow: isActive
                  ? '0 4px 12px rgba(0, 85, 55, 0.12)'
                  : '0 1px 3px rgba(0, 0, 0, 0.02)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#0284c7';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateX(0)';
                }
              }}
            >
              <Space size={12}>
                <Tag
                  color={isActive ? '#005537' : 'blue'}
                  style={{
                    borderRadius: 6,
                    fontWeight: 700,
                    padding: '3px 10px',
                    fontSize: 12.5,
                    border: 'none',
                  }}
                >
                  Phần {p.no}
                </Tag>
                <span
                  style={{
                    fontSize: 13.5,
                    color: isActive ? '#005537' : '#1e293b',
                    fontWeight: isActive ? 700 : 600,
                  }}
                >
                  {p.title}
                </span>
              </Space>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isActive && (
                  <Tag
                    className="lesson-toc-active-tag"
                    color="success"
                    icon={<CheckCircleFilled />}
                    style={{ borderRadius: 6, fontWeight: 700, fontSize: 11.5 }}
                  >
                    Đang xem
                  </Tag>
                )}
                <RightOutlined
                  style={{
                    fontSize: 12,
                    color: isActive ? '#005537' : '#94a3b8',
                    transition: 'transform 0.2s',
                  }}
                />
              </div>
            </div>
          );
        })}

        {/* Summary Row */}
        {hasSummary && (
          <div
            className="lesson-toc-row"
            onClick={handleSummaryClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '11px 16px',
              borderRadius: 10,
              cursor: 'pointer',
              background: activePartNo === 'summary' ? '#fefce8' : '#fafafa',
              border: activePartNo === 'summary' ? '2px solid #ca8a04' : '1px dashed #cbd5e1',
              transition: 'all 0.2s ease',
              marginTop: 4,
            }}
            onMouseEnter={(e) => {
              if (activePartNo !== 'summary') {
                e.currentTarget.style.background = '#fefce8';
                e.currentTarget.style.borderColor = '#ca8a04';
                e.currentTarget.style.transform = 'translateX(4px)';
              }
            }}
            onMouseLeave={(e) => {
              if (activePartNo !== 'summary') {
                e.currentTarget.style.background = '#fafafa';
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.transform = 'translateX(0)';
              }
            }}
          >
            <Space size={12}>
              <Tag
                color={activePartNo === 'summary' ? '#ca8a04' : 'gold'}
                icon={<TrophyOutlined />}
                style={{
                  borderRadius: 6,
                  fontWeight: 700,
                  padding: '3px 10px',
                  fontSize: 12.5,
                  border: 'none',
                }}
              >
                Tổng kết
              </Tag>
              <span
                style={{
                  fontSize: 13.5,
                  color: activePartNo === 'summary' ? '#854d0e' : '#475569',
                  fontWeight: activePartNo === 'summary' ? 700 : 600,
                }}
              >
                Tóm tắt 1 trang & Ghi nhớ thực chiến
              </span>
            </Space>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {activePartNo === 'summary' && (
                <Tag className="lesson-toc-active-tag" color="warning" style={{ borderRadius: 6, fontWeight: 700, fontSize: 11.5 }}>
                  Đang xem
                </Tag>
              )}
              <RightOutlined
                style={{
                  fontSize: 12,
                  color: activePartNo === 'summary' ? '#ca8a04' : '#94a3b8',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
